import { makeAutoObservable } from "mobx";
import { Location, NavigateFunction } from "react-router-dom";
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils";
import OrgGuardStore from "./OrgGuardStore";
import { messageError } from "reusable/mui/modals/message-error";

import { routes, RouteItem, RoutePermission, getDefaultRoutes } from './main-container/RouteConfig'
import { Route, StageEnum, notNull } from "@gap/utils/utils";
import variables from "variables/variables";
import { regionCache } from "cache/regionCache";
import { Menu, menu } from "./main-container/MenuConfig";
import { CacheHandler } from "reusable/utils/cache";
import { deferAction } from "reusable/utils/utils";
import { HomeOutlined } from "@mui/icons-material";
import RegionRouterStore from "features/main/RegionRouterStore";

interface RouterStoreParams {
   navigate: NavigateFunction
   location: Location
   orgGuardStore: OrgGuardStore
   regionRouterStore: RegionRouterStore
}

interface NavOptions {
   suffix?: string | undefined
   orgId?: string | undefined
}

export default class RouterStore {
   sp
   region: string
   user
   goTo = Object.fromEntries(
      Object.keys(routes).map((key) => [
         key,
         () => this.goToNavPath(key as Route)
      ])
   ) as Record<keyof typeof routes, () => void>

   constructor(sp: RouterStoreParams) {
      makeAutoObservable(this)
      this.sp = sp

      // All routes in here are coming from /(region)/
      this.region = sp.location.pathname.split("/")[1]
      const cachedRegion = regionCache.region.get()
      this.user = sp.orgGuardStore.userData
      if (cachedRegion && cachedRegion !== this.region) {
         // If the region is set and not the same as the current, wipe cache and reload.
         CacheHandler.clearAll()
         sessionStorage.clear()
         window.location.reload()
         return
      }
      regionCache.region.set(this.region)
   }

   get defaultRoutes() {
      return getDefaultRoutes(this.sp.orgGuardStore.userData.userOrg)
   }

   // For each main menu item, go through it's children(items) and check their permissions.
   get menu() {
      const filteredMenu: Menu[] = []

      if (this.sp.orgGuardStore.userOrg.isDeactivated) {
         return this.noAccessMenu
      }

      for (const menuSection of menu) {
         // Hide Learn menu section if user doesn't have Gap Academy access
         if (menuSection.title === 'Learn' && !this.sp.orgGuardStore.showGapAcademy) {
            continue
         }

         const filteredMenuItems = menuSection.children.filter(menuItem => !menuItem.permission || (menuItem.permission && this.hasPermissions([menuItem.permission])))
         if (filteredMenuItems.length) {
            filteredMenu.push({
               title: menuSection.title,
               children: filteredMenuItems
            })
         }
      }

      return filteredMenu
   }

   get noAccessMenu(): Menu[] {
      return [{
         title: '',
         children: [
            {
               title: 'Home',
               icon: HomeOutlined,
               action: {
                  route: routes['no-access']
               },
            }
         ]
      }]
   }

   // Filter routes by routes you have access to
   get routes() {
      if (this.sp.orgGuardStore.userOrg.isDeactivated) {
         return Object.values(routes).filter(route => route.canAccessAsADeactivatedUser)
      }

      return Object.values(routes).filter(route => !route.permissions?.length || this.hasPermissions(route.permissions))
   }

   hasPermissions(permissions: RoutePermission[]): boolean {
      return !permissions.length || permissions.some(({ featureId, actionId, onlyForStages, onlyForOrgs, developerOnly, devOrProductTeamOnly, featureToggle, hideWhenFeatureToggle, additionalConditions }) => {
         if (featureId) {
            const feature = this.sp.orgGuardStore.userData.userOrg?.features.find(f => f.featureId === featureId)
            if (!feature || (actionId && !feature.actions.some(a => a.actionId === actionId))) {
               return false
            }
         }

         // Return false if current stage is not in the list, or if onlyForStages includes dev/preview/feature/bug and our stage doesnt start with those
         if (onlyForStages?.length && !onlyForStages.includes(variables.stage as StageEnum)) {
            const hasDevMatch = onlyForStages.includes('dev') && notNull(variables.stage).startsWith('dev')
            const hasPreviewMatch = onlyForStages.includes('preview') && notNull(variables.stage).startsWith('preview')
            const hasFeatureMatch = onlyForStages.includes('feature') && notNull(variables.stage).startsWith('feature')
            const hasRcMatch = onlyForStages.includes('rc') && notNull(variables.stage).startsWith('rc')
            const hasBugMatch = onlyForStages.includes('bug') && notNull(variables.stage).startsWith('bug')
            if (!(hasDevMatch || hasPreviewMatch || hasFeatureMatch || hasRcMatch || hasBugMatch)) {
               return false
            }
         }

         if (onlyForOrgs && (!(!!this.sp.orgGuardStore.userData.userOrg) || !onlyForOrgs.includes(this.sp.orgGuardStore.userOrg.orgType))) {
            return false
         }

         if (developerOnly && !this.sp.orgGuardStore.isDeveloper) {
            return false
         }

         if (devOrProductTeamOnly && !this.sp.orgGuardStore.isDevOrProductTeam) {
            return false
         }

         if (featureToggle && !this.sp.orgGuardStore.hasFeatureToggleAccess(featureToggle)) {
            return false
         }

         if (hideWhenFeatureToggle && this.sp.orgGuardStore.shouldHideForFeatureToggle(hideWhenFeatureToggle)) {
            return false
         }

         if (additionalConditions && !additionalConditions(this.sp.orgGuardStore.userData.userOrg)) {
            return false
         }

         return true
      })
   }

   getNavPath(routePath?: string, options: NavOptions = {}): string {
      const region = this.region // ex. au
      const orgPrefix = this.sp.orgGuardStore.getOrgPrefix(options.orgId) //ex. org/01HFSJ7JS77C2RE7N25068X7HA
      routePath = routePath && routePath[0] === '/' ? routePath?.substring(1) : routePath // convert /work-items to work-items. We add the slash (/)
      const suffix = (!!options.suffix ? (options.suffix[0] === '/' ? options.suffix.substring(1) : options.suffix) : '') // Remove first char if it is a slash (/)

      // /au/00gap
      let path = `/${region}/${orgPrefix}`

      // /au/00gap/processes
      path += `/${routePath}`

      if (suffix && suffix.length) {
         // /au/00gap/processes/00gap/01HTXMWJAHCV7QEX8G8724N98S
         path += `/${suffix}`
      }

      return path
   }

   goToNavPath(routePath?: Route | '/', options?: NavOptions) {
      const path = this.getNavPath(routePath, options)
      this.sp.navigate(path)
   }

   /**
    * Document templates live at the standalone `document-templates` route for gap
    * users, but under the `templates/documents` tab for advisor users. Route to the
    * right place based on the current org type.
    */
   goToDocumentTemplates(suffix?: string) {
      if (this.sp.orgGuardStore.userOrg.orgType === 'advisor') {
         this.goToNavPath('templates/documents', { suffix })
      } else {
         this.navigateToRoute(routes['document-templates'], suffix)
      }
   }

   navigateToRoute(route: RouteItem, suffix?: string) {
      if (!route.permissions?.length || this.hasPermissions(route.permissions)) {
         const path = this.getNavPath(route.path, { suffix })
         deferAction(() => this.sp.navigate(path))
      } else {
         // A user wont really see this error
         messageError("You dont have enough access rights to view this route", "Access denied")
      }
   }

   navigate(route: string, asIs?: boolean) {
      this.sp.navigate(asIs ? route : this.getNavPath(route))
   }

   navigateToOrgsDefault = () => {
      const route = this.defaultRoutes[this.sp.orgGuardStore.userOrg.orgType]
      this.navigateToRoute(route)
   }
}

export const { Provider: RouterStoreProvider, useProvidedStore: useRouterStore, useOptionalStore: useOptionalRouterStore } =
   createStoreContext(RouterStore)
